import { i18nMark } from '@lingui/core'
import { t } from '@lingui/macro'
import { colors, Tooltip } from '@material-ui/core'
import { formatDistanceStrict } from 'date-fns'
import { css } from 'emotion'
import React from 'react'
import {
  EAccountStatus,
  getAccountStatus,
  IAccount,
} from 'shopify-payment-core'

import { localeOptions } from '../helpers/formats'
import { i18n } from '../i18n'

interface IProps {
  account: IAccount
}

const statusLabels = {
  [EAccountStatus.ACTIVE]: i18nMark('Aktivní'),
  [EAccountStatus.BLOCKED]: i18nMark('Blokovaný'),
  [EAccountStatus.EXPIRED]: i18nMark('Neuhrazeno'),
  [EAccountStatus.NEW]: i18nMark('Nový'),
  [EAccountStatus.TRIAL]: i18nMark('Trial'),
  [EAccountStatus.NOPLAN]: i18nMark('Bez plánu'),
  [EAccountStatus.NOINVOICE]: i18nMark('Bez fakturace'),
  [EAccountStatus.DUEDATE]: i18nMark('Po splatnosti'),
}

const statusColors = {
  [EAccountStatus.ACTIVE]: colors.green[500],
  [EAccountStatus.BLOCKED]: colors.grey[500],
  [EAccountStatus.EXPIRED]: colors.deepOrange[400],
  [EAccountStatus.NEW]: colors.lightBlue[300],
  [EAccountStatus.TRIAL]: colors.lightGreen[500],
  [EAccountStatus.NOPLAN]: colors.lightGreen[500],
  [EAccountStatus.NOINVOICE]: colors.lightGreen[500],
  [EAccountStatus.DUEDATE]: colors.red[700],
}

export const AccountStatusDisplay: React.FC<{
  status: EAccountStatus
  tooltip?: string
}> = ({ status, tooltip = '' }) => {
  const label = i18n._(statusLabels[status])
  const color = statusColors[status]
  return (
    <Tooltip
      title={tooltip}
      enterDelay={500}
      classes={{
        tooltip: css`
          font-size: 1rem;
        `,
      }}
    >
      <span style={{ color }}>{label}</span>
    </Tooltip>
  )
}

export const AccountStatus: React.FC<IProps> = ({ account }) => {
  const status = getAccountStatus(account)
  const statusTooltip = {
    [EAccountStatus.ACTIVE]: i18n._(t`Aktivně využívaný účet.`),
    [EAccountStatus.BLOCKED]: i18n._(
      t`Důvod blokace: ${account.blockedReason}`,
    ),
    [EAccountStatus.EXPIRED]: i18n._(
      t`Trial platby jsou vyčerpány, nové platby neprojdou dokud nebude zaplacena první faktura.`,
    ),
    [EAccountStatus.NEW]: i18n._(
      t`Nově registrovaný účet, nemá vyplněné údaje brány.`,
    ),
    [EAccountStatus.TRIAL]: i18n._(
      t`Trial plateb zbývá: ${account.trialRemaining}`,
    ),
    [EAccountStatus.NOPLAN]: i18n._(
      t`Účet je aktivní, ale nemá zvolený platební plán`,
    ),
    [EAccountStatus.NOINVOICE]: i18n._(
      t`Účet je aktivní, ale nemá nastavené datum fakturace`,
    ),
    [EAccountStatus.DUEDATE]: i18n._(
      t`Fakturace je ${formatDistanceStrict(
        new Date(),
        account.invoiceDueDate!,
        localeOptions,
      )} po splatnosti.`,
    ),
  }
  const tooltip = i18n._(statusTooltip[status])
  return <AccountStatusDisplay status={status} tooltip={tooltip} />
}
